// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The debug trail the phone keeps for itself, compactly, and ships only on a trigger.
//
// In normal play the hole screen produces one or two debug lines per inbound socket message, and
// until 2026-09 every one of them left the phone as its own log.emit frame — 9,349 uplink frames
// in one iPhone round, most of them "Received websocket message". Those same lines were what made
// that round's battery drain diagnosable, so they are kept, not dropped: here, in memory, with no
// storage writes and no periodic upload. They leave the phone when someone needs them — a bug
// report, an error-level log, the debug flag turning live logging on, or the round's summary
// opening — and then all at once, over a socket the trigger has already woken. See appLogger.ts
// for the shipping side.
//
// Compact on purpose: a trail is mostly the same few lines repeated — "Received websocket
// message" with the same round, hole and trace for every broadcast on a hole — so a line is
// stored as a numeric time, a level and two references, and identical messages and attribute
// sets are kept once and shared. A round's worth costs a few hundred kilobytes rather than the
// megabytes the full objects would. The sharing tables are caches, not the store: a line holds
// its own references, so a table can be cleared at any time (it is, on every drain and whenever
// it outgrows the buffer) without losing a line.

import type { LogEmitCommand } from './bffWebSocket';

type LogLevel = NonNullable<LogEmitCommand['level']>;

/** A line as the logger hands it over. */
export type BufferedLogInput = {
  message: string;
  level: LogLevel;
  attributes: Record<string, unknown> | undefined;
  /** Milliseconds since the epoch, when the line was produced. */
  at: number;
};

/** A line as it leaves the buffer, ready for a log frame. */
export type BufferedLogEntry = {
  message: string;
  level: LogLevel;
  attributes: Record<string, unknown> | undefined;
  /** Milliseconds since the epoch, when the line was produced. */
  at: number;
  /** The same moment as ISO text, so a flushed line keeps its own time rather than the flush's. */
  timestamp: string;
};

/**
 * A round's worth. With one hole screen per device (#588) the 2026-09-13 round produced about
 * 1,650 debug lines; a busy tournament round may still overflow, and then the oldest go first.
 */
export const DEBUG_LOG_BUFFER_CAPACITY = 3000;

type Slot = {
  at: number;
  level: LogLevel;
  message: string;
  attributes: Record<string, unknown> | undefined;
};

const slots: (Slot | undefined)[] = new Array<Slot | undefined>(DEBUG_LOG_BUFFER_CAPACITY);
let head = 0;
let size = 0;
let dropped = 0;

// A cache never needs more distinct values than the buffer holds lines.
const messages = new Map<string, string>();
const attributeSets = new Map<string, Record<string, unknown>>();

const shared = <T>(cache: Map<string, T>, key: string, value: T): T => {
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  if (cache.size >= DEBUG_LOG_BUFFER_CAPACITY) cache.clear();
  cache.set(key, value);
  return value;
};

const sharedAttributes = (
  attributes: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!attributes) return undefined;
  try {
    return shared(attributeSets, JSON.stringify(attributes), attributes);
  } catch {
    // Not serialisable (a cycle): kept as it is, unshared. It would not survive the frame either.
    return attributes;
  }
};

const reset = (): void => {
  slots.fill(undefined);
  head = 0;
  size = 0;
  dropped = 0;
  messages.clear();
  attributeSets.clear();
};

/** Keeps the line, letting the oldest go once the buffer is full. */
export const bufferDebugLog = ({ message, level, attributes, at }: BufferedLogInput): void => {
  const slot: Slot = {
    at,
    level,
    message: shared(messages, message, message),
    attributes: sharedAttributes(attributes),
  };
  if (size === DEBUG_LOG_BUFFER_CAPACITY) {
    slots[head] = slot;
    head = (head + 1) % DEBUG_LOG_BUFFER_CAPACITY;
    dropped += 1;
    return;
  }
  slots[(head + size) % DEBUG_LOG_BUFFER_CAPACITY] = slot;
  size += 1;
};

/** Everything buffered, oldest first, plus how many older lines were let go; empties the buffer. */
export const drainDebugLogs = (): { entries: BufferedLogEntry[]; dropped: number } => {
  const entries: BufferedLogEntry[] = [];
  for (let i = 0; i < size; i += 1) {
    const slot = slots[(head + i) % DEBUG_LOG_BUFFER_CAPACITY];
    if (!slot) continue;
    entries.push({
      message: slot.message,
      level: slot.level,
      attributes: slot.attributes,
      at: slot.at,
      timestamp: new Date(slot.at).toISOString(),
    });
  }
  const result = { entries, dropped };
  reset();
  return result;
};

export const debugLogBufferSize = (): number => size;

export const __resetDebugLogBufferForTests = reset;
